import { html, css } from 'lit';
import { RecommendationItem, Recommendations } from '../../server/types.js';
import { canFavorite, getItemBadge, trackClickEvent } from './utils/index.js';
import './recommendation-badge.js';
import './favorite-heart.js';
import { i18n } from '@lingui/core';
import WarpElement from '@warp-ds/elements-core';
import { customElement } from 'lit/decorators/custom-element.js';

@customElement('recommendation-card')
export class RecommendationCard extends WarpElement {
	static styles = [
		...WarpElement.styles,
		css`
			.custom-label-style {
				background: linear-gradient(
					transparent,
					rgba(0, 0, 0, 0.04935) 13.71%,
					/* ... (rest of the gradient styles) */ rgba(0, 0, 0, 0.78731) 89.73%,
					rgba(0, 0, 0, 0.8)
				);
				width: 100%;
			}
			.two-lines {
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 2;
				overflow: hidden;
			}
			.one-line {
				display: -webkit-box;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 1;
				overflow: hidden;
			}

			/** The line below is where the injected CSS goes, removing it means you get no CSS from the design system **/
			*,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.text-body{font-size:var(--w-font-size-m);line-height:var(--w-line-height-m)}.aspect-square{padding-bottom:100%;position:relative}.aspect-square>*{width:100%;height:100%;position:absolute;inset:0}.rounded-t-8{border-top-left-radius:8px;border-top-right-radius:8px}.hover\:no-underline:hover{text-decoration:none}.order-first{order:-9999}.order-2{order:2}.order-3{order:3}.order-4{order:4}.static{position:static}.hover\:s-bg-hover:hover{background-color:var(--w-s-color-background-hover)}.active\:s-bg-active:active{background-color:var(--w-s-color-background-active)}.m-16{margin:1.6rem}.-mx-4{margin-left:-.4rem;margin-right:-.4rem}.mt-6{margin-top:.6rem}.px-4{padding-left:.4rem;padding-right:.4rem}.pb-10{padding-bottom:1rem}.pb-2{padding-bottom:.2rem}.pb-28{padding-bottom:2.8rem}.pl-10{padding-left:1rem}.pr-16{padding-right:1.6rem}.pt-6{padding-top:.6rem}.pt-\[88px\]{padding-top:88px}.break-all{word-break:break-all}.object-cover{object-fit:cover}.object-scale-down{object-fit:scale-down}.object-center{object-position:center}.text-ellipsis{text-overflow:ellipsis}@media (width>=480px){.sm\:h-auto{height:auto}};
		`,
	];

	item!: RecommendationItem;
	data!: Recommendations;
	imgCdnUrl!: string;
	favoriteBaseUrl!: string;
	loginId!: string;
	index!: number;

	static properties = {
		item: { type: Object },
		data: { type: Object },
		imgCdnUrl: { type: String },
		favoriteBaseUrl: { type: String },
		loginId: { type: String },
		locale: { type: String },
		index: { type: Number },
	};

	private handleCardClick(e: MouseEvent) {
		const target = e.target as HTMLElement;
		const isInteractive = target.closest('a') || target.closest('favorite-heart');

		const url = this.item.url;
		if (!isInteractive && typeof url === 'string' && url.length > 0) {
			trackClickEvent(this.item, this.data);
			if (e.metaKey || e.ctrlKey || e.button === 1) {
				window.open(url, '_blank');
			} else {
				window.location.href = url;
			}
		}
	}

	render() {
		return html`
			<article class="relative" aria-labelledby="${`card-title-${this.item.itemId}-${this.index}`}">
				<w-card @click="${this.handleCardClick}">
					<div class="relative flex flex-col">
						<div class="m-16 flex flex-col">
							<!-- 1. Title -->
							<h3 id="${`card-title-${this.item.itemId}-${this.index}`}" class="text-body font-normal order-3 px-4 pt-6 pb-2 mb-0 -mx-4">
								<a
									class="s-text hover:no-underline text-md text-ellipsis two-lines break-words pointer-events-auto"
									href="${this.item.url}"
									@click="${() => trackClickEvent(this.item, this.data)}"
								>
									${this.item.heading}
								</a>
							</h3>
							<span class="sr-only absolute top-0 left-0">
							<!-- 2. Badge -->
								<recommendation-badge
									.badge="${getItemBadge(this.item.type, this.item.overlay, this.item.shippingOption)}"
									locale="${this.locale}"
								></recommendation-badge>
							</span>
							<!-- 3. Price -->
							<span class="sr-only">
								${this.item.type?.toUpperCase() !== 'JOB'
									? html` <div class="absolute bottom-0 pb-28">${this.item.label ? this.item.label : ''}</div> `
									: ''}
							</span>
							<!-- 4. Additional info -->
							<div class="text-s s-text-subtle mt-6 order-4">
								${this.item.content?.company_name
									? html` <div class="text-ellipsis one-line break-all">${this.item.content?.company_name}</div>`
									: ''}
								${this.item.customLabel ? html` <div>${this.item.customLabel}</div>` : ''}
							</div>
							<!-- 5. Location -->
							<div class="text-s s-text-subtle break-words text-ellipsis two-lines order-2">
								<span>${this.item.location?.combined}</span>
							</div>
						</div>

						<!-- 6. Image -->
						<div class="order-first">
							${this.renderImage()}
							<recommendation-badge
								aria-hidden="true"
								.badge="${getItemBadge(this.item.type, this.item.overlay, this.item.shippingOption)}"
								locale="${this.locale}"
							></recommendation-badge>
						</div>
					</div>
				</w-card>
				<!-- 7. Favorite button -->
				${canFavorite(this.item)
					? html` <favorite-heart
							.item="${this.item}"
							.favoriteBaseUrl="${this.favoriteBaseUrl}"
							.loginId="${this.loginId}"
							locale="${this.locale}"
						></favorite-heart>`
					: ''}
			</article>
		`;
	}

	private renderImage() {
		if (!this.item.image) return;
		const isLogo = this.item.image.type === 'LOGO';
		const imageClass = isLogo
			? 'w-full h-full object-center object-scale-down m-auto sm:h-auto'
			: 'w-full h-full object-center object-cover m-auto';
		const imageSrc =
			this.item.image.isAbsolute || this.item.image.type === 'EXTERNAL'
				? this.item.image.url
				: `${this.imgCdnUrl}/${this.item.image.url}`;

		return html`
			<div class="aspect-square overflow-hidden border rounded-t-8 s-bg hover:s-bg-hover active:s-bg-active">
				<img
					class="${imageClass}"
					src="${imageSrc}"
					alt="${i18n.t({ id: 'frontpage.recommendations.images.alt.text', message: 'Photo from ad' })}"
				/>
				${this.item.type && this.item.type.toUpperCase() !== 'JOB' && this.item.type.toUpperCase() !== 'CMS'
					? html`
							<span aria-hidden="true">
								<div
									class="absolute bottom-0 font-bold s-text-inverted-static pt-[88px] pb-10 pl-10 pr-16 custom-label-style"
								>
									${this.item.label ? this.item.label : ''}
								</div>
							</span>
						`
					: ''}
			</div>
		`;
	}
}
